import { AgentOverview } from "@flypengine/myFlyp-lib"
import React from "react"
import { ChevronRight, Close, ExternalLink } from "../../../components/Icon"
import { agentStage } from "../../../lib/format"
import * as A from "fp-ts/Array"
import { pipe } from "fp-ts/lib/function"
import * as O from "fp-ts/Option"
import useCarrousel from "../../../hooks/useCarrousel"
import { boolean } from "fp-ts"

type Props = {
  agents: AgentOverview[]
  onClickMsg: () => void
}

const stageLabel = (agent: AgentOverview): string =>
  pipe(
    agent.agentDealStatus,
    O.filter(s => s === "ACCEPTED"),
    O.filter(
      () =>
        agent.stage === "instructionSent" ||
        agent.stage === "instructionDownloaded"
    ),
    O.map(() => "Instruction Accepted"),
    O.getOrElse(() => agentStage(agent.stage))
  )

export default function view({ agents, onClickMsg }: Props): JSX.Element {
  const container = React.useRef<HTMLDivElement>(null)
  const prevButton = React.useRef<HTMLButtonElement>(null)
  const nextButton = React.useRef<HTMLButtonElement>(null)

  const { scrollContainerBy } = useCarrousel({
    containerRef: container,
    prevButtonRef: prevButton,
    nextButtonRef: nextButton,
  })

  return (
    <div
      className="card py-4 flex flex-col gap-3 "
      data-cy="dashboard-agents-list"
    >
      <div className="flex items-center">
        <h2 className="heading grow">Agents instructed</h2>
        {agents.length > 1 && (
          <>
            <button
              ref={prevButton}
              className="button-icon hidden md:block rotate-180 disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("left")}
              data-cy="dashboard-agents-list-scroll-left"
            >
              <ChevronRight />
            </button>
            <button
              ref={nextButton}
              className=" ml-1 button-icon hidden md:block disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("right")}
              data-cy="dashboard-agents-list-scroll-right"
            >
              <ChevronRight />
            </button>
          </>
        )}
        <button
          className="ml-2 w-8 h-8 "
          onClick={onClickMsg}
          data-cy="dashboard-agents-list-close"
        >
          <Close />
        </button>
      </div>
      <div ref={container} className="grow carousel">
        {pipe(
          agents,
          A.filterMapWithIndex((idx, agent) =>
            O.some(
              <div
                className={`card px-4 py-[6px] w-48 flex-[0_0_auto] ${pipe(
                  agent.agentDealStatus,
                  O.exists(status => status === "ACCEPTED"),
                  boolean.fold(
                    () => "bg-tile",
                    () => "bg-lawn"
                  )
                )}`}
                key={idx}
                data-cy={`dashboard-agents-list-${idx}`}
              >
                <h5 className=" mb-1 heading text-xl leading-tight text-cutoff">
                  {agent.brand}
                </h5>
                <p className=" text-xs text-cutoff leading-[130%] ">
                  {O.getOrElse(() => "")(agent.branch)}
                </p>
                <p className="text-xs text-cutoff leading-[130%] ">
                  {pipe(
                    agent.website,
                    O.match(
                      () => <>{stageLabel(agent)}</>,
                      website =>
                        agent.stage === "publishedOnAgentWebsite" ? (
                          <a
                            href={website}
                            className="text-xs flex"
                            target="_blank"
                            data-cy={`dashboard-agents-list-${idx}-website`}
                          >
                            <span className="mr-2 hover:underline">
                              {agentStage(agent.stage)}
                            </span>
                            <span className="inline-block w-3 h-3 text-black">
                              <ExternalLink />
                            </span>
                          </a>
                        ) : (
                          <>{stageLabel(agent)}</>
                        )
                    )
                  )}
                </p>
              </div>
            )
          )
        )}
      </div>
    </div>
  )
}
